import React, { useEffect, useRef, useState } from 'react';
import { ROOMS } from '../rooms';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { serverNow, cityListen } from '../services/worldSocket';
import { audioEngine } from '../services/audioEngine';
import { takeAudio, releaseAudio } from '../services/audioPool';
import { overKeys } from './hudStack';

// RoomStoryAudio — the voices of a City Story scene that plays INSIDE this room
// (RoomStory draws the bodies). The street's CityTalk is unmounted indoors and is
// tuned to street distances, so a room plays its own scene, at room level:
// every line on the episode's clock (a late arrival hears the right line), the
// song that scores it underneath, and a caption at the bottom of the screen.
//
// Being in here with an inside scene coming up is LISTENING, so the server makes
// the voices (its lazy rule): `city_listen` for that story, off when you leave.
// DOM, outside the Canvas.
const LISTEN_EARLY_MS = 6 * 60000;   // voices are made at the five-minute freeze
const VOICE_LEVEL = 0.95;
const SCORE_LEVEL = 0.3;
const SCORE_UNDER = 0.12;

const capWrap = {
  position: 'fixed', left: '50%', bottom: overKeys(), transition: 'bottom 0.35s ease',   // over the WASD keys when they're open (hudStack)
  transform: 'translateX(-50%)', zIndex: 35, maxWidth: 'min(560px, 88vw)', pointerEvents: 'none', textAlign: 'center',
  background: 'rgba(10,8,12,0.78)', border: '1px solid rgba(255,178,94,0.35)', borderRadius: 14, padding: '8px 16px 9px',
  color: '#ffe9c2', font: '15px system-ui, sans-serif', lineHeight: 1.4, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
};

export default function RoomStoryAudio() {
  const zoom = useWorldStore((s) => s.zoom);
  const interior = useWorldStore((s) => s.interior);
  // Any room: a scene plays here only if a story says so (its scenes' `r`).
  const room = zoom === ZOOM.INTERIOR && ROOMS[interior] ? interior : null;
  const [cap, setCap] = useState(null);
  const voices = useRef(new Map());   // `${story}:${idx}` → { el, started }
  const scores = useRef(new Map());   // story id → { el, started }
  const listening = useRef(new Set());

  useEffect(() => {
    if (!room) return undefined;
    let ducked = false;
    const tick = () => {
      const t = serverNow();
      const cs = useCityStore.getState();
      const nameOf = (id) => cs.byId?.[id]?.name || (cs.snap?.residents || []).find((r) => String(r.avatarId) === String(id))?.name || '';
      const keep = new Set(); const keepScore = new Set(); const listen = new Set();
      let caption = null; let lineOn = false;
      for (const st of Object.values(cs.stories || {})) {
        const mine = (st.scenes || []).filter((sc) => sc.r === room);
        if (!mine.length) continue;
        if (t >= mine[0].t0 - LISTEN_EARLY_MS && t <= mine[mine.length - 1].t1) listen.add(`story:${st.id}`);
        const sc = mine.find((x) => t >= x.t0 && t < x.t1);
        if (!sc) continue;
        for (const l of st.lines || []) {
          if (l.scene !== sc.i) continue;
          const end = l.playAt + (l.dur || 3) * 1000;
          const key = `${st.id}:${l.idx}`;
          if (l.audio && t >= l.playAt - 4000 && t < end + 400) {
            keep.add(key);
            let v = voices.current.get(key);
            // Borrowed from the pool: on iOS only a tap-blessed element may start (services/audioPool).
            if (!v) { v = { el: takeAudio(l.audio), started: false }; voices.current.set(key, v); }
            if (t >= l.playAt && !v.started) {
              v.started = true;
              try { v.el.currentTime = Math.max(0, (t - l.playAt) / 1000); } catch { /* seek when ready */ }
              v.el.volume = VOICE_LEVEL;
              v.el.play().catch(() => {});
            }
          }
          if (t >= l.playAt && t < end) {
            lineOn = true;
            caption = { name: nameOf(l.speaker), text: l.text };
            try { cs.setSpeaking?.(l.speaker, end); } catch { /* the body just won't mouth it */ }
          }
        }
        // The song that scores it, on the episode's clock, under the voices.
        if (st.score?.url && t >= st.t0 && t <= st.t1) {
          keepScore.add(st.id);
          let s = scores.current.get(st.id);
          if (!s) { s = { el: takeAudio(st.score.url), started: false }; scores.current.set(st.id, s); }
          const want = (t - st.t0) / 1000;
          if (!s.started) {
            s.started = true;
            try { s.el.currentTime = want; } catch { /* seek on metadata */ }
            s.el.addEventListener('loadedmetadata', () => { try { s.el.currentTime = (serverNow() - st.t0) / 1000; } catch { /* ignore */ } }, { once: true });
            s.el.play().catch(() => {});
          } else if (s.el.readyState >= 2 && Math.abs(s.el.currentTime - want) > 0.8) {
            try { s.el.currentTime = want; } catch { /* ignore */ }
          }
          const fade = Math.max(0, Math.min(1, (st.t1 - t) / 3000));
          s.el.volume = Math.max(0, Math.min(1, (lineOn ? SCORE_UNDER : SCORE_LEVEL) * fade));
        }
      }
      // Listening: the server voices what someone is here to hear.
      for (const k of listen) if (!listening.current.has(k)) { listening.current.add(k); cityListen(k, true); }
      for (const k of [...listening.current]) if (!listen.has(k)) { listening.current.delete(k); cityListen(k, false); }
      for (const [k, v] of voices.current) if (!keep.has(k)) { releaseAudio(v.el); voices.current.delete(k); }
      for (const [k, s] of scores.current) if (!keepScore.has(k)) { releaseAudio(s.el); scores.current.delete(k); }
      // The room's music steps back while a scene is on (standard duck, not silence).
      const on = lineOn || keepScore.size > 0;
      if (on !== ducked) { ducked = on; try { audioEngine.duck(on); } catch { /* no engine */ } }
      setCap((c) => (c?.text === caption?.text && c?.name === caption?.name ? c : caption));
    };
    const id = setInterval(tick, 150);
    tick();
    return () => {
      clearInterval(id);
      for (const v of voices.current.values()) { releaseAudio(v.el); }
      for (const s of scores.current.values()) { releaseAudio(s.el); }
      voices.current.clear(); scores.current.clear();
      for (const k of listening.current) cityListen(k, false);
      listening.current.clear();
      if (ducked) { try { audioEngine.duck(false); } catch { /* no engine */ } }
      setCap(null);
    };
  }, [room]);

  if (!room || !cap) return null;
  return (
    <div style={capWrap} role="status" aria-live="polite">
      {cap.name && <div style={{ fontSize: 10.5, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.7, marginBottom: 2 }}>{cap.name}</div>}
      <div>{cap.text}</div>
    </div>
  );
}
